<template>
  <div ref="wrapperRef" class="m-directory-wrapper">
    <div class="m-directory-container">
      <InputText v-model="directoryPath" :disabled="disabled || readonly" :placeholder="placeholder"
        class="m-directory-input" readonly @blur="handleBlur" />
      <Button icon="pi pi-folder-open" :disabled="disabled || readonly" class="m-directory-button"
        @click="openDirectoryPicker" />
    </div>

    <div v-if="hint" class="m-directory-hint">
      {{ hint }}
    </div>

    <FieldVerifyErrorOverlay :messages="normalizedErrors" :anchor-el="wrapperRef" />
  </div>
</template>

<script setup lang="ts">
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import FieldVerifyErrorOverlay from '@/components/ui/FieldVerifyErrorOverlay.vue'
import { computed, ref } from 'vue'

interface Props {
  modelValue?: string | number | null
  placeholder?: string
  hint?: string
  disabled?: boolean
  readonly?: boolean
  errorMessages?: string | string[]
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  placeholder: 'Выберите папку',
  hint: '',
  disabled: false,
  readonly: false,
  errorMessages: () => []
})

const emit = defineEmits<{
  'update:modelValue': [value: string]
  'change': [value: string]
  'blur': []
}>()

const wrapperRef = ref<HTMLElement | null>(null)

const directoryPath = computed({
  get: () => {
    // Convert number to string, handle null/undefined
    if (props.modelValue === null || props.modelValue === undefined) {
      return ''
    }
    return String(props.modelValue)
  },
  set: (value) => {
    emit('update:modelValue', value)
    emit('change', value)
  }
})

const normalizedErrors = computed(() => {
  if (Array.isArray(props.errorMessages)) return props.errorMessages
  return [props.errorMessages]
})

const openDirectoryPicker = async () => {
  try {
    // Modern browser API
    if ('showDirectoryPicker' in window) {
      const dirHandle = await (window as any).showDirectoryPicker()
      directoryPath.value = dirHandle.name
    } else {
      // Fallback: show input dialog
      const path = prompt('Введите путь к папке:', directoryPath.value)
      if (path !== null) {
        directoryPath.value = path
      }
    }
  } catch (error) {
    console.warn('Directory picker cancelled or not supported:', error)
  }
}

const handleBlur = () => {
  emit('blur')
}
</script>

<style scoped lang="scss">
.m-directory-wrapper {
  width: 100%;
}

.m-directory-container {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.m-directory-input {
  flex: 1;
}

.m-directory-button {
  flex-shrink: 0;
  width: 40px;
  height: 40px;

  &:deep(.p-button) {
    padding: 0;
    width: 100%;
    height: 100%;
  }
}

.m-directory-hint {
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 0.5rem;
}
</style>
